import { Button, Card } from '@citizen-walk/shared-ui';
import { doc, getDoc, type DocumentData } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { db } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import { useDistrict } from '../districts-admin';
import { StreetSurveyItemsSelector } from './StreetSurveyItemsSelector';
import { toStreetSurveyPost } from './useStreetSurveyPosts';
import { useStreetSurveyConfig } from './useStreetSurveyConfig';
import { StreetSurveyImageBoard } from './StreetSurveyImageBoard';
import { StreetSurveyInsightPanel } from './StreetSurveyInsightPanel';
import { StreetSurveyPanorama } from './StreetSurveyPanorama';
import { StreetSurveyUploadPanorama } from './StreetSurveyUploadPanorama';
import { StreetSurveyRankedList } from './StreetSurveyRankedList';
import { StreetSurveyExtractionRecordCard } from './StreetSurveyExtractionRecordCard';
import { StreetSurveyAnalysisHistoryCard } from './StreetSurveyAnalysisHistoryCard';
import { StreetSurveySurveysPanel } from './StreetSurveySurveysPanel';
import { useStreetSurveySelection } from './streetSurveySurveys';
import { resolveMasterRow } from './streetSurveyPinDisplay';

/**
 * ストリートビュー机上調査 詳細レポート永続ページ (docs/25 Increment 2)。
 * `/districts/:districtId/street-survey/:postId` — 一覧画面の「詳細レポートを別タブで開く」から遷移。
 *
 * 永続リンクとして開かれる想定 (別タブ) のため onSnapshot ではなく単発 getDoc で取得する。
 *
 * 対象外 (今回は実装しない):
 *   - グラウンドトゥルースの補足（実地評価）フォーム — 明示的に後回し
 *   - 解析履歴テーブル — 省略
 */
type LoadState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'ready'; id: string; data: DocumentData };

export function StreetSurveyDetailPage() {
  const { districtId, postId } = useParams<{ districtId: string; postId: string }>();
  const { state: authState } = useAdminAuth();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [highlightedRank, setHighlightedRank] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'2d' | '3d'>('2d');

  // 検出要素の選択 (highlightedRank) を、ページ内のどこかをクリックしたら解除する
  // (2026-07-21、ユーザー要望)。ランキングリスト項目・3Dパノラマのピンの onClick 側で
  // stopPropagation しているため、それらのクリックはここまで伝播しない (選択が維持される)。
  // 特定のラッパー要素の onClick ではなく document レベルで監視することで、コンテンツ領域外の
  // ページ余白をクリックした場合も含め、確実に解除されるようにしている。
  useEffect(() => {
    function handleDocumentClick() {
      setHighlightedRank(null);
    }
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  useEffect(() => {
    if (!districtId || !postId) return;
    let cancelled = false;
    setState({ status: 'loading' });
    getDoc(doc(db, 'districts', districtId, 'posts', postId))
      .then((snap) => {
        if (cancelled) return;
        if (!snap.exists()) {
          setState({ status: 'not-found' });
          return;
        }
        setState({ status: 'ready', id: snap.id, data: snap.data() });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'not-found' });
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, postId]);

  // 解析結果 (classifications 等) はモード非依存の出力フォーマットから動的に解決する必要があるため、
  // 先に raw doc の settingsId で実効設定を解決してから toStreetSurveyPost を組み立てる
  // (post 自体を作ってから settingsId を読む鶏卵問題を回避)。
  const rawSettingsId =
    state.status === 'ready'
      ? (((state.data.aiAnalysis as Record<string, unknown> | undefined)?.settingsId as
          | string
          | undefined) ?? null)
      : null;
  // docs/27 §9: 地区の上書きを反映した実効設定から、結果の読み取り・調査項目・評価軸を解決する
  // (2026-09-16: 読み取りもテンプレートの M3 ではなく実効スキーマで行う、docs/98 R-98 高 1)。
  const districtState = useDistrict(districtId ?? null);
  const district = districtState.status === 'ready' ? districtState.district : null;
  // docs/98 R-97: 表示する調査 (URL の ?survey=、一覧ページから引き継ぐ)。
  const surveySelection = useStreetSurveySelection(district);
  const surveyKey = surveySelection.surveyKey;
  const surveyConfig = useStreetSurveyConfig(
    district,
    rawSettingsId ?? district?.aiAnalysis?.settingsId ?? null,
    surveyKey,
  );
  const post = useMemo(
    () =>
      state.status === 'ready'
        ? toStreetSurveyPost(
            state.id,
            state.data,
            surveyConfig.schema,
            surveyConfig.display.result,
            surveyKey,
          )
        : null,
    [state, surveyConfig.schema, surveyConfig.display.result, surveyKey],
  );
  // 調査項目は一覧ページと同じく切り替えられる (未選択なら地区の既定、docs/27 §9-2 (d))。
  const [legendAxisKeyState, setLegendAxisKey] = useState<string | null>(null);
  const [aggregateAxisKeyState, setAggregateAxisKey] = useState<string | null>(null);
  const legendAxisKey = legendAxisKeyState ?? surveyConfig.defaultLegendAxisKey;
  const aggregateAxisKey = aggregateAxisKeyState ?? surveyConfig.defaultAggregateAxisKey;
  const primaryAxis = useMemo(
    () => surveyConfig.asPrimary(aggregateAxisKey),
    [surveyConfig, aggregateAxisKey],
  );
  const roleTypeAxis = useMemo(
    () => surveyConfig.asRole(legendAxisKey),
    [surveyConfig, legendAxisKey],
  );

  return (
    <>
      <header className="mb-6">
        <Link
          to={
            districtId
              ? `/districts/${districtId}/street-survey${surveySelection.querySuffix}`
              : '/districts'
          }
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← ストリートビュー机上調査
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 mt-2">詳細レポート</h1>
      </header>
      {districtId && (
        <StreetSurveySurveysPanel
          districtId={districtId}
          district={district}
          selection={surveySelection}
          canManage={false}
          uid={null}
        />
      )}

      {state.status === 'loading' && (
        <Card padding="md">
          <p className="text-sm text-slate-500">読込中…</p>
        </Card>
      )}
      {state.status === 'not-found' && (
        <Card padding="md" className="border-danger-600">
          <p className="text-sm text-danger-700">
            投稿が見つかりません (id: <code>{postId}</code>)。
          </p>
        </Card>
      )}

      {post && (
        <>
          <p className="text-xs text-slate-500 mb-2 flex flex-wrap gap-x-3 gap-y-1">
            <span>Location ID: {post.id}</span>
            <span>
              座標: {post.location.latitude.toFixed(6)}, {post.location.longitude.toFixed(6)}
            </span>
            {post.streetView?.imageryDate && (
              <span>StreetView撮影: {post.streetView.imageryDate}</span>
            )}
          </p>

          {districtId && (
            <StreetSurveyItemsSelector
              districtId={districtId}
              config={surveyConfig}
              legendAxisKey={legendAxisKey}
              aggregateAxisKey={aggregateAxisKey}
              onChangeLegend={setLegendAxisKey}
              onChangeAggregate={setAggregateAxisKey}
              canSaveDefault={authState.role === 'master'}
              uid={authState.user?.uid ?? null}
              surveyKey={surveyKey}
              district={district}
            />
          )}

          <StreetSurveyInsightPanel post={post} />
          <StreetSurveyExtractionRecordCard
            record={post.streetView?.extraction}
            config={surveyConfig}
          />
          <StreetSurveyAnalysisHistoryCard
            districtId={districtId!}
            postId={post.id}
            currentRunId={post.analysisRunId}
            evaluation={surveyConfig.evaluation}
            valueField={primaryAxis.primaryAxis?.codeField ?? null}
            valueLabel={primaryAxis.primaryAxis?.label ?? '調査項目'}
            labelOf={(v) => resolveMasterRow(v, primaryAxis.byCategoryCd)?.category ?? v}
            surveyKey={surveyKey}
            additionalSurveyKeys={surveySelection.options
              .filter((o) => !o.isDefault)
              .map((o) => o.key)}
          />

          {post.streetView && (
            <>
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={viewMode === '2d' ? 'primary' : 'secondary'}
                  onClick={() => setViewMode('2d')}
                >
                  2D画像ボード
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === '3d' ? 'primary' : 'secondary'}
                  onClick={() => setViewMode('3d')}
                >
                  3Dパノラマ
                </Button>
              </div>
              {viewMode === '2d' ? (
                <StreetSurveyImageBoard
                  districtId={districtId!}
                  post={post}
                  primaryAxis={primaryAxis}
                  roleTypeAxis={roleTypeAxis}
                  highlightedRank={highlightedRank}
                  reasoningField={surveyConfig.schema?.reasoningField}
                  enableHoverTooltip
                />
              ) : post.provider === 'upload-360' ? (
                // docs/27 §6-3: 登録済み 360度写真は自前の全天球ビューアに重畳する。
                <StreetSurveyUploadPanorama
                  post={post}
                  primaryAxis={primaryAxis}
                  roleTypeAxis={roleTypeAxis}
                  highlightedRank={highlightedRank}
                  onSelectRank={setHighlightedRank}
                  reasoningField={surveyConfig.schema?.reasoningField}
                />
              ) : (
                <StreetSurveyPanorama
                  districtId={districtId!}
                  post={post}
                  primaryAxis={primaryAxis}
                  roleTypeAxis={roleTypeAxis}
                  highlightedRank={highlightedRank}
                  onSelectRank={setHighlightedRank}
                  reasoningField={surveyConfig.schema?.reasoningField}
                />
              )}
            </>
          )}
          {post.provider === 'upload-360' && post.analysisStatus !== 'done' && (
            <Card padding="md" className="mb-3 border-amber-300 bg-amber-50">
              <p className="text-sm text-amber-800">
                この360°写真はまだAI解析されていません。机上調査ページで解析対象に選んで実行してください。
              </p>
            </Card>
          )}

          <StreetSurveyRankedList
            post={post}
            classifications={post.classifications}
            primaryAxis={primaryAxis}
            axes={surveyConfig.axes}
            roleTypeAxis={roleTypeAxis}
            highlightedRank={highlightedRank}
            onSelectRank={setHighlightedRank}
            reasoningField={surveyConfig.schema?.reasoningField}
            evaluation={surveyConfig.evaluation}
            listFieldKeys={surveyConfig.display.listFieldKeys}
          />
        </>
      )}
    </>
  );
}
